/**
 * Mobile touch controls — virtual joystick (left) + camera swipe (right).
 * Writes directly into the shared `input` object each frame.
 *
 * No pointer-lock needed. Works alongside the keyboard path on desktop
 * (just never fires unless touch events appear).
 */

import { input, triggerMobileE } from '../core/input.js';

export const IS_MOBILE =
    ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
    !/Windows NT.*Touch/i.test(navigator.userAgent); // Surface/hybrid → treat as desktop

const TOUCH_LOOK_SCALE = 0.0032; // rad per pixel

// ── Joystick state ────────────────────────────────────────────────────────
let joyId      = -1;
let joyBaseX   = 0, joyBaseY   = 0;
let joyMoveX   = 0, joyMoveZ   = 0;

// ── Look swipe state ──────────────────────────────────────────────────────
let lookId     = -1;
let lookLastX  = 0, lookLastY = 0;

// ── DOM references ────────────────────────────────────────────────────────
let base, knob, enterBtn, chatToggle;

const MAX_STICK = 52; // pixels — max joystick travel radius

export function initMobileControls(canvas) {
    if (!IS_MOBILE) return;

    document.body.classList.add('is-mobile');

    base     = document.getElementById('joy-base');
    knob     = document.getElementById('joy-knob');
    enterBtn = document.getElementById('mobile-enter');
    chatToggle = document.getElementById('chat-toggle');

    canvas.addEventListener('touchstart',  onTouchStart,  { passive: false });
    canvas.addEventListener('touchmove',   onTouchMove,   { passive: false });
    canvas.addEventListener('touchend',    onTouchEnd,    { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd,    { passive: false });

    // Portal enter button
    if (enterBtn) {
        enterBtn.addEventListener('touchstart', (e) => {
            e.stopPropagation();
            triggerMobileE();
        }, { passive: false });
    }
}

/** Called every frame from main.js — overrides pollInput() results if joystick active. */
export function applyTouchInput() {
    if (!IS_MOBILE) return;
    if (joyId !== -1) {
        input.moveX  = joyMoveX;
        input.moveZ  = joyMoveZ;
        input.moving = joyMoveX !== 0 || joyMoveZ !== 0;
    }
}

/** Show/hide the mobile Enter button based on portal proximity. */
export function setEnterVisible(visible) {
    if (!IS_MOBILE || !enterBtn) return;
    enterBtn.style.opacity = visible ? '1' : '0';
    enterBtn.style.pointerEvents = visible ? 'auto' : 'none';
}

// ── Internal touch handlers ───────────────────────────────────────────────

function onTouchStart(e) {
    e.preventDefault();
    for (const t of e.changedTouches) {
        const isLeft = t.clientX < window.innerWidth * 0.52;

        if (isLeft && joyId === -1) {
            joyId    = t.identifier;
            joyBaseX = t.clientX;
            joyBaseY = t.clientY;
            joyMoveX = 0; joyMoveZ = 0;
            if (base) {
                base.style.display = 'block';
                base.style.left    = joyBaseX + 'px';
                base.style.top     = joyBaseY + 'px';
                knob.style.transform = 'translate(-50%,-50%)';
            }
        } else if (!isLeft && lookId === -1) {
            lookId    = t.identifier;
            lookLastX = t.clientX;
            lookLastY = t.clientY;
        }
    }
}

function onTouchMove(e) {
    e.preventDefault();
    for (const t of e.changedTouches) {
        if (t.identifier === joyId) {
            const dx   = t.clientX - joyBaseX;
            const dy   = t.clientY - joyBaseY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const r    = Math.min(dist, MAX_STICK) / MAX_STICK;
            const ang  = Math.atan2(dy, dx);

            joyMoveX = Math.cos(ang) * r;          // strafe
            joyMoveZ = -Math.sin(ang) * r;          // forward (up = forward)

            if (knob) {
                const kx = Math.cos(ang) * Math.min(dist, MAX_STICK);
                const ky = Math.sin(ang) * Math.min(dist, MAX_STICK);
                knob.style.transform = `translate(calc(-50% + ${kx}px), calc(-50% + ${ky}px))`;
            }
        } else if (t.identifier === lookId) {
            const dx = t.clientX - lookLastX;
            const dy = t.clientY - lookLastY;
            input.lookX += dx * TOUCH_LOOK_SCALE;
            input.lookY += dy * TOUCH_LOOK_SCALE;
            lookLastX = t.clientX;
            lookLastY = t.clientY;
        }
    }
}

function onTouchEnd(e) {
    e.preventDefault();
    for (const t of e.changedTouches) {
        if (t.identifier === joyId) {
            joyId    = -1;
            joyMoveX = 0; joyMoveZ = 0;
            if (base) base.style.display = 'none';
        } else if (t.identifier === lookId) {
            lookId = -1;
        }
    }
}
